import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { toMs } from './usageTiers';

// The per-account "comment" shown in the admin Billing view.
//
// Storage is the existing admin-only `accountNotes/{accountUUID}/notes`
// collection, with comment revisions marked `kind: 'comment'`. That collection
// is deliberately top-level (not under accountsInfo) so the account holder can
// never read it, and it is append-only by rule — no update, no delete. So an
// "edit" appends a new revision and we read the newest; every earlier revision
// survives as the comment's history, which is exactly what the UI wants.
//
// Query shape matters: a LONE equality filter (`kind == 'comment'`) is served
// by Firestore's automatic single-field index. Do NOT add an orderBy — that
// would make it a composite query and require an index deploy. The newest
// revision is picked client-side instead; the revision count per account is
// tiny.
//
// If this per-account fan-out ever becomes slow at fleet size, the drop-in
// replacement is one admin endpoint returning the same map, built server-side
// with the Admin SDK. Don't reach for that until it's actually needed.

const COMMENT_KIND = 'comment';
// Cap on simultaneous in-flight reads, so opening the view on a large fleet
// doesn't fire hundreds of parallel requests at once.
const CONCURRENCY = 25;

/** Newest-first revision list for one account. */
async function fetchComment(accountUUID) {
  const snap = await getDocs(query(
    collection(db, 'accountNotes', accountUUID, 'notes'),
    where('kind', '==', COMMENT_KIND),
  ));
  const revisions = snap.docs
    .map((d) => {
      const data = d.data();
      return {
        id: d.id,
        text: data.text || '',
        adminEmail: data.adminEmail || null,
        // A revision written moments ago has a null serverTimestamp until the
        // write lands; treat it as "now" so it still sorts newest.
        createdAtMs: toMs(data.createdAt),
      };
    })
    .sort((a, b) => (b.createdAtMs ?? Infinity) - (a.createdAtMs ?? Infinity));

  const [latest, ...history] = revisions;
  return latest
    ? { text: latest.text, createdAtMs: latest.createdAtMs, adminEmail: latest.adminEmail, history }
    : null;
}

/** Run `worker` over `items` with a bounded number in flight. */
async function mapLimit(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const i = cursor++;
      results[i] = await worker(items[i], i);
    }
  });
  await Promise.all(runners);
  return results;
}

/**
 * useAccountComments(enabled, accountIds)
 *
 * enabled    — only fetch while the Billing view is on screen
 * accountIds — account UUIDs to load comments for
 *
 * Returns { comments, loading, error, unavailable, refreshOne }
 *   comments    — { [uuid]: { text, createdAtMs, adminEmail, history: [...] } }
 *   error       — some or all reads failed; last-good data is kept
 *   unavailable — EVERY read failed, so editing should be disabled too
 *   failedIds   — Set of accounts whose latest read threw; their cells keep
 *                 whatever was last read and must not be presented as empty
 *   refreshOne  — (uuid) => Promise, re-reads one account after an edit
 */
export function useAccountComments(enabled, accountIds) {
  const [comments, setComments] = useState({});
  // The id set whose fan-out has completed. Deriving `loading` from this
  // (rather than a flag flipped inside the effect) means the very first render
  // after the view opens already reports loading — otherwise every cell would
  // flash "Add comment…" for one frame before the reads even start.
  const [loadedKey, setLoadedKey] = useState(null);
  const [error, setError] = useState(false);
  // Every read failed — almost always rules/auth, so editing is pointless too.
  // A PARTIAL failure leaves editing enabled: writes are independent of reads,
  // and a failed save surfaces its own error in the cell.
  const [unavailable, setUnavailable] = useState(false);
  // Accounts whose most recent read threw. Their cells stay non-editable and
  // say so, instead of silently reading as "no comment".
  const [failedIds, setFailedIds] = useState(() => new Set());

  // Stable dependency: the array identity changes on every parent render.
  const idsKey = useMemo(() => (accountIds || []).join(','), [accountIds]);

  // Guards against a slow earlier fan-out overwriting newer results.
  const runIdRef = useRef(0);
  // Must be re-armed on every mount, not just the first: React 18 StrictMode
  // mounts → unmounts → re-mounts in development, so a flag that is only ever
  // cleared would stay false for the component's whole life and silently
  // suppress every state update below.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const ids = idsKey ? idsKey.split(',') : [];
    if (ids.length === 0) {
      setComments({});
      setLoadedKey(idsKey);
      return;
    }

    const runId = ++runIdRef.current;
    (async () => {
      try {
        const results = await mapLimit(ids, CONCURRENCY, async (uuid) => {
          try {
            return [uuid, await fetchComment(uuid)];
          } catch (e) {
            // One unreadable account must not blank the whole column.
            console.warn(`useAccountComments: failed to read comment for ${uuid}:`, e.code || e.message);
            return [uuid, undefined];
          }
        });
        if (!mountedRef.current || runId !== runIdRef.current) return;
        // `undefined` = this account's read threw; `null` = read fine, no
        // comment. A failed read must NOT drop the comment we already hold —
        // a blank, editable cell has to mean "no comment", never "we could not
        // find out", or an admin types over wording they were never shown.
        // Classify BEFORE dispatching any state: a useState updater is only
        // required to be pure, and React may run it during the render phase
        // rather than at dispatch, so nothing below may depend on the updater
        // having already run. The updater's only job is to merge last-good
        // entries; every flag is derived from `failed`, which is final here.
        const failed = new Set();
        const fresh = {};
        results.forEach(([uuid, value]) => {
          if (value === undefined) failed.add(uuid);
          else if (value) fresh[uuid] = value;
        });
        setComments((prev) => {
          const next = { ...fresh };
          failed.forEach((uuid) => { if (prev[uuid]) next[uuid] = prev[uuid]; });
          return next;
        });
        setFailedIds(failed);
        setError(failed.size > 0);
        setUnavailable(failed.size === ids.length);
      } catch (e) {
        console.warn('useAccountComments: comment load failed:', e.code || e.message);
        if (mountedRef.current && runId === runIdRef.current) {
          setError(true);
          setUnavailable(true);
        }
      } finally {
        // Mark this id set done either way — a failed load is still "no longer
        // loading", and the error flags carry the failure.
        if (mountedRef.current && runId === runIdRef.current) setLoadedKey(idsKey);
      }
    })();
  }, [enabled, idsKey]);

  const loading = enabled && idsKey !== loadedKey;

  const refreshOne = useCallback(async (accountUUID) => {
    try {
      const value = await fetchComment(accountUUID);
      if (!mountedRef.current) return;
      setComments((prev) => {
        const next = { ...prev };
        if (value) next[accountUUID] = value;
        else delete next[accountUUID];
        return next;
      });
      // A successful re-read clears this account's failed mark.
      setFailedIds((prev) => {
        if (!prev.has(accountUUID)) return prev;
        const next = new Set(prev);
        next.delete(accountUUID);
        return next;
      });
    } catch (e) {
      console.warn(`useAccountComments: refresh failed for ${accountUUID}:`, e.code || e.message);
      if (mountedRef.current) {
        setError(true);
        setFailedIds((prev) => new Set(prev).add(accountUUID));
      }
    }
  }, []);

  return { comments, loading, error, unavailable, failedIds, refreshOne };
}

export { COMMENT_KIND };
